<script>
</script>

<svelte:head>
	<title>Terms of Service - OS Studio</title>
	<meta name="description" content="OS Studio Terms of Service. Please read these terms carefully before using our platform." />
</svelte:head>

<div class="min-h-screen bg-white">
	<!-- Header -->
	<div class="bg-gradient-to-br from-purple-900 to-blue-900 text-white py-16">
		<div class="container mx-auto px-4 max-w-4xl">
			<h1 class="text-5xl font-bold mb-4">Terms of Service</h1>
			<p class="text-xl text-purple-100">Last Updated: February 2026</p>
		</div>
	</div>

	<!-- Content -->
	<div class="container mx-auto px-4 max-w-4xl py-16">
		<div class="prose prose-lg max-w-none">
			<h2>1. Agreement to Terms</h2>

			<p>
				By accessing and using OS Studio ("Platform," "Service," "we," "us," or "our"), you accept and agree to be bound by and comply with these Terms of Service ("Terms"). If you do not agree to abide by the above, please do not use this service.
			</p>

			<h2>2. Use License</h2>

			<p>
				Subject to your compliance with these Terms, we grant you a limited, non-exclusive, non-transferable, revocable license to:
			</p>
			<ul>
				<li>Access and use the OS Studio platform</li>
				<li>Generate code, specifications, and configurations for your own business purposes</li>
				<li>Deploy generated systems on your infrastructure or approved managed deployments</li>
			</ul>

			<h3>2.1 Restrictions</h3>

			<p>You may NOT:</p>
			<ul>
				<li>Reproduce, duplicate, copy, or sell the OS Studio platform or access to it</li>
				<li>Reverse engineer, decompile, or attempt to derive the source code or algorithms</li>
				<li>Modify, adapt, translate, or create derivative works of the platform</li>
				<li>Use the platform for any illegal purpose or in violation of any laws or regulations</li>
				<li>Use the platform to develop competing products or services</li>
				<li>Remove, obscure, or alter any proprietary notices or labels</li>
				<li>Use automated tools (bots, scrapers, crawlers) without written permission</li>
				<li>Attempt to gain unauthorized access to the platform or related systems</li>
				<li>Resell or redistribute access to the platform without authorization</li>
			</ul>

			<h2>3. Generated Code Ownership & Rights</h2>

			<h3>3.1 Your Ownership</h3>

			<p>
				<strong>OS Studio generates code, architectures, specifications, and configurations exclusively for your use.</strong> You own all generated code and intellectual property, including:
			</p>
			<ul>
				<li>Source code and compiled binaries</li>
				<li>System architecture and design specifications</li>
				<li>Infrastructure-as-Code templates and configuration files</li>
				<li>Database schemas and migrations</li>
				<li>API documentation and tests</li>
				<li>Any modifications or extensions you make</li>
			</ul>

			<h3>3.2 Your Rights</h3>

			<p>
				You have the right to:
			</p>
			<ul>
				<li>Use generated code for any lawful purpose (commercial, non-commercial, internal, external)</li>
				<li>Modify, adapt, and extend generated code</li>
				<li>Distribute, sublicense, or sell systems built with generated code</li>
				<li>Use code as a foundation for competitive products</li>
				<li>Export and migrate your systems away from OS Studio at any time</li>
			</ul>

			<h3>3.3 No Vendor Lock-In</h3>

			<p>
				We do not claim any rights to code you generate. Once generated, your code is yours forever. If you stop using OS Studio, you retain all rights to the code you generated and can continue using and distributing it without restrictions.
			</p>

			<h2>4. Infrastructure & Deployment Options</h2>

			<h3>4.1 Self-Hosted Deployment</h3>

			<p>
				If you choose self-hosted deployment:
			</p>
			<ul>
				<li>You maintain complete ownership and control of your infrastructure</li>
				<li>OS Studio provides monitoring and optional security guidance</li>
				<li>You are responsible for infrastructure costs, maintenance, security, and compliance</li>
				<li>OS Studio has no access to your systems except through authorized API calls</li>
			</ul>

			<h3>4.2 Managed Deployment</h3>

			<p>
				If you choose managed deployment (with your own cloud account):
			</p>
			<ul>
				<li>You provide cloud account credentials (AWS, GCP, Azure, etc.)</li>
				<li>You retain 100% ownership of the AWS/GCP/Azure account and all resources</li>
				<li>OS Studio deploys and manages your system within your account</li>
				<li>You maintain full administrative access and can revoke OS Studio access at any time</li>
				<li>You pay cloud provider costs directly; OS Studio is a separate service fee</li>
				<li>You can export and migrate away at any time without data loss</li>
			</ul>

			<h3>4.3 OS Studio Managed Infrastructure</h3>

			<p>
				If OS Studio provides fully managed infrastructure (optional):
			</p>
			<ul>
				<li>OS Studio manages deployment, scaling, monitoring, and security</li>
				<li>You retain ownership and full control of your deployed system</li>
				<li>You can request a data export and migrate to your own infrastructure</li>
				<li>Termination results in data export before account closure</li>
			</ul>

			<h2>5. Service Availability & Support</h2>

			<h3>5.1 Availability</h3>

			<p>
				We strive for 99.9% uptime on the OS Studio platform. However, we make no guarantees. The platform may be unavailable for:
			</p>
			<ul>
				<li>Scheduled maintenance (with advance notice when possible)</li>
				<li>Emergency security patches</li>
				<li>Unforeseeable technical issues or third-party failures</li>
			</ul>

			<h3>5.2 Support</h3>

			<p>
				Support is provided according to your plan:
			</p>
			<ul>
				<li><strong>Free Plan:</strong> Community support and documentation</li>
				<li><strong>Pro Plan:</strong> Email support (24-48 hour response)</li>
				<li><strong>Enterprise:</strong> Dedicated support and SLA</li>
			</ul>

			<h2>6. Fees & Payment</h2>

			<h3>6.1 Subscription Fees</h3>

			<p>
				OS Studio operates on a subscription basis. Fees vary by plan and are detailed on our pricing page. You agree to pay all applicable fees in full.
			</p>

			<h3>6.2 Billing</h3>

			<ul>
				<li>Monthly subscriptions renew automatically on the anniversary date</li>
				<li>Annual subscriptions receive a discount</li>
				<li>Cancellation takes effect at the end of your billing cycle</li>
				<li>No refunds for partial months; usage is prorated for upgrades</li>
			</ul>

			<h3>6.3 Price Changes</h3>

			<p>
				We may change prices with 30 days' written notice. Changes apply to new subscriptions and renewals. Existing subscriptions honor the current price through renewal.
			</p>

			<h2>7. Prohibited Activities</h2>

			<p>
				You agree NOT to use the OS Studio platform for:
			</p>
			<ul>
				<li>Illegal activities or content</li>
				<li>Harassment, threats, or abuse of others</li>
				<li>Creating malware, viruses, or exploits</li>
				<li>Phishing, fraud, or deception</li>
				<li>Denial-of-service (DoS) attacks</li>
				<li>Intellectual property infringement</li>
				<li>Building weapons, surveillance systems, or tools for illegal hacking</li>
				<li>Generating code intended to harm individuals or organizations</li>
				<li>Child exploitation or abuse material</li>
				<li>Violating export control or sanctions laws</li>
			</ul>

			<h2>8. Acceptable Use Policy</h2>

			<p>
				You represent and warrant that:
			</p>
			<ul>
				<li>You are of legal age and have the authority to enter into this agreement</li>
				<li>You will use the platform only for lawful purposes</li>
				<li>You will not violate any applicable laws or third-party rights</li>
				<li>You will not interfere with platform security or functionality</li>
				<li>You own or have rights to any data you provide</li>
			</ul>

			<h2>9. Limitation of Liability</h2>

			<h3>9.1 Disclaimer</h3>

			<p>
				THE PLATFORM IS PROVIDED "AS-IS" AND "AS-AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, OR NON-INFRINGEMENT.
			</p>

			<h3>9.2 Liability Cap</h3>

			<p>
				EXCEPT FOR GROSS NEGLIGENCE OR WILLFUL MISCONDUCT, OS STUDIO'S TOTAL LIABILITY FOR ANY CLAIM WILL NOT EXCEED YOUR TOTAL FEES PAID IN THE 12 MONTHS PRECEDING THE CLAIM.
			</p>

			<h3>9.3 Excluded Damages</h3>

			<p>
				IN NO EVENT SHALL OS STUDIO BE LIABLE FOR:
			</p>
			<ul>
				<li>Indirect, incidental, or consequential damages</li>
				<li>Lost profits, revenue, or business opportunities</li>
				<li>Reputational harm</li>
				<li>Data loss or corruption</li>
				<li>Even if OS Studio has been advised of the possibility of such damages</li>
			</ul>

			<h2>10. Indemnification</h2>

			<p>
				You agree to indemnify, defend, and hold harmless OS Studio from any claims, damages, losses, costs, or expenses arising from:
			</p>
			<ul>
				<li>Your use of the platform in violation of these Terms</li>
				<li>Your generated code or systems</li>
				<li>Your violation of any laws or third-party rights</li>
				<li>Your use for prohibited activities</li>
			</ul>

			<h2>11. Intellectual Property Rights</h2>

			<h3>11.1 OS Studio IP</h3>

			<p>
				All OS Studio platform code, designs, features, algorithms, and documentation are our exclusive property. You may not copy, reverse engineer, or claim ownership of platform IP.
			</p>

			<h3>11.2 Your Generated Code</h3>

			<p>
				You own all generated code and configurations. OS Studio retains no rights to code you generate. See Section 3 for details.
			</p>

			<h2>12. Data & Privacy</h2>

			<p>
				Your use of OS Studio is governed by our Privacy Policy. We handle your data according to GDPR, CCPA, and other applicable laws. For details, see our Privacy Policy.
			</p>

			<h2>13. Account Termination</h2>

			<h3>13.1 Termination by You</h3>

			<p>
				You may terminate your account at any time by contacting support. Upon termination:
			</p>
			<ul>
				<li>You retain ownership of all generated code</li>
				<li>Your data will be available for export for 30 days</li>
				<li>We will securely delete your data after 90 days</li>
				<li>No refunds are provided for unused subscription time</li>
			</ul>

			<h3>13.2 Termination by OS Studio</h3>

			<p>
				We may terminate your account for:
			</p>
			<ul>
				<li>Violation of these Terms</li>
				<li>Illegal activity or prohibited use</li>
				<li>Non-payment of fees (after 30-day notice)</li>
				<li>Abuse of platform resources</li>
			</ul>

			<p>
				If terminated, you have 30 days to export your data and generated code.
			</p>

			<h2>14. Modifications to Terms</h2>

			<p>
				We may modify these Terms at any time. Material changes will be communicated via email or a notice on the platform. Continued use of OS Studio constitutes acceptance of updated Terms.
			</p>

			<h2>15. Governing Law & Dispute Resolution</h2>

			<h3>15.1 Governing Law</h3>

			<p>
				These Terms are governed by the laws of [Your Jurisdiction], without regard to conflicts of law principles.
			</p>

			<h3>15.2 Dispute Resolution</h3>

			<p>
				Before litigation, you and OS Studio agree to attempt to resolve disputes through good-faith negotiation. If unresolved within 30 days, disputes shall be resolved through binding arbitration according to the rules of [Arbitration Body].
			</p>

			<h2>16. Severability</h2>

			<p>
				If any provision of these Terms is found invalid, the remaining provisions continue in effect.
			</p>

			<h2>17. Contact & Support</h2>

			<p>
				For questions about these Terms, contact us at:
			</p>
			<ul>
				<li><strong>Email:</strong> <a href="mailto:legal@osstudio.com">legal@osstudio.com</a></li>
				<li><strong>Support:</strong> <a href="mailto:support@osstudio.com">support@osstudio.com</a></li>
			</ul>

		</div>
	</div>
</div>

<style lang="postcss">
	:global(body) {
		margin: 0;
		font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
		background: white;
		color: #1a1a1a;
	}

	:global(html, body) {
		width: 100%;
	}

	:global(.prose) {
		--prose-body: #374151;
		--prose-headings: #111827;
		--prose-links: #9333ea;
	}

	:global(.prose h2) {
		@apply text-3xl font-bold text-gray-900 mt-12 mb-4;
	}

	:global(.prose h3) {
		@apply text-2xl font-bold text-gray-900 mt-8 mb-3;
	}

	:global(.prose p) {
		@apply text-gray-700 mb-4 leading-relaxed;
	}

	:global(.prose ul) {
		@apply list-disc list-inside mb-4 space-y-2;
	}

	:global(.prose ul li) {
		@apply text-gray-700;
	}

	:global(.prose a) {
		@apply text-purple-600 font-semibold;
	}

	:global(.prose a:hover) {
		@apply text-purple-700;
	}
</style>
